import { MSOPractice, PracticeCluster } from '../types';
import { microRegions } from '../data/msoData';

export type { PracticeCluster };

/**
 * Group practices into their regional clusters
 * Returns aggregated cluster data for map visualization
 */
export const aggregatePracticeClusters = (practices: MSOPractice[]): PracticeCluster[] => {
  // Group practices by cluster assignment
  const practicesByCluster = new Map<string, MSOPractice[]>();

  practices.forEach((practice) => {
    const clusterId = practice.clusterAssignment;
    if (!clusterId) return; // Skip practices without cluster assignment

    if (!practicesByCluster.has(clusterId)) {
      practicesByCluster.set(clusterId, []);
    }
    practicesByCluster.get(clusterId)!.push(practice);
  });

  // Create cluster objects with aggregated data
  return microRegions.map((region) => {
    const clusterPractices = practicesByCluster.get(region.id) || [];

    // Calculate totals
    const totalRevenue = clusterPractices.reduce((sum, p) => sum + p.revenue, 0);
    const avgRevenue = clusterPractices.length > 0
      ? totalRevenue / clusterPractices.length
      : 0;

    // Count specialties
    const specialtyMix: Record<string, number> = {};
    clusterPractices.forEach((practice) => {
      const specialty = practice.specialty;
      specialtyMix[specialty] = (specialtyMix[specialty] || 0) + 1;
    });

    return {
      id: region.id,
      name: region.name,
      center: region.center,
      state: region.state,
      practiceCount: clusterPractices.length,
      practices: clusterPractices,
      totalRevenue: parseFloat(totalRevenue.toFixed(2)),
      avgRevenue: parseFloat(avgRevenue.toFixed(2)),
      specialtyMix,
    };
  }).filter(cluster => cluster.practiceCount > 0); // Only include clusters with practices
};

/**
 * Get practices within a specific cluster
 */
export const getPracticesInCluster = (
  practices: MSOPractice[],
  clusterId: string
): MSOPractice[] => {
  return practices.filter((practice) => practice.clusterAssignment === clusterId);
};

/**
 * Calculate distance between two coordinates (in miles)
 * Uses Haversine formula
 */
export const calculateDistance = (
  coord1: [number, number],
  coord2: [number, number]
): number => {
  const [lon1, lat1] = coord1;
  const [lon2, lat2] = coord2;

  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) *
    Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return parseFloat(distance.toFixed(2));
};

/**
 * Get cluster statistics for display
 */
export const getClusterStats = (cluster: PracticeCluster) => {
  return {
    practiceCount: cluster.practiceCount,
    totalRevenue: `$${cluster.totalRevenue.toFixed(1)}M`,
    avgRevenue: `$${cluster.avgRevenue.toFixed(1)}M`,
    topSpecialty: Object.entries(cluster.specialtyMix)
      .sort(([, a], [, b]) => b - a)[0]?.[0] || 'N/A',
    specialties: Object.keys(cluster.specialtyMix).length,
  };
};
